/**
 * AssignTasksStep — Step 3 of the Job Management Wizard
 *
 * Select a category, then add/remove/reorder tasks assigned to it.
 */

import { Can } from '@/domains/access-control';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { useCan } from '@skewb/access-control/react';
import { ArrowDown, ArrowUp, Plus, Search, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import {
  useAssignTask,
  useCategoryTasks,
  useJobCategories,
  useRemoveTaskAssignment,
  useReorderTasks,
  useTaskTypes,
  useUpdateCategoryTask,
} from '../hooks/useJobManagement';

export default function AssignTasksStep() {
  const { data: categoriesData } = useJobCategories();
  const { data: taskTypesData } = useTaskTypes();

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [removeConfirmId, setRemoveConfirmId] = useState<number | null>(null);
  const [taskSearch, setTaskSearch] = useState('');

  // WRK-5670: category→task assignment has its own CategoryTask subject.
  const canReadTasks = useCan('read', 'CategoryTask');
  const { data: categoryTasksData, isLoading: tasksLoading } = useCategoryTasks(
    selectedCategoryId,
    { enabled: canReadTasks },
  );
  const assignMutation = useAssignTask();
  const removeMutation = useRemoveTaskAssignment();
  const reorderMutation = useReorderTasks();
  const updateMutation = useUpdateCategoryTask();
  const canToggleMandatory = useCan('update', 'CategoryTask');
  // The Actions column holds reorder (update) + remove (delete). Drop it when the role has
  // neither, keeping this table consistent with the other config tables (WRK-5669).
  const canReorder = useCan('update', 'CategoryTask');
  const canRemove = useCan('delete', 'CategoryTask');
  const showActions = canReorder || canRemove;

  const categories = categoriesData?.data ?? [];
  const allTaskTypes = taskTypesData?.data ?? [];
  const assignedTasks = categoryTasksData?.data ?? [];

  // Task types not yet assigned to this category
  const availableTaskTypes = useMemo(() => {
    const assignedIds = new Set(assignedTasks.map((t) => t.taskTypeId));
    let available = allTaskTypes.filter((tt) => !assignedIds.has(tt.id));
    if (taskSearch) {
      const q = taskSearch.toLowerCase();
      available = available.filter((tt) => tt.name?.toLowerCase().includes(q));
    }
    return available;
  }, [allTaskTypes, assignedTasks, taskSearch]);

  async function handleAssign(taskTypeId: number) {
    if (!selectedCategoryId) return;
    await assignMutation.mutateAsync({
      jobCategoryId: selectedCategoryId,
      taskTypeId,
      orderNumber: String(assignedTasks.length + 1),
    });
  }

  async function handleToggleMandatory(id: number, isMandatory: boolean) {
    if (!selectedCategoryId) return;
    await updateMutation.mutateAsync({ id, categoryId: selectedCategoryId, isMandatory });
  }

  async function handleRemove(id: number) {
    if (!selectedCategoryId) return;
    await removeMutation.mutateAsync({ id, categoryId: selectedCategoryId });
    setRemoveConfirmId(null);
  }

  const moveTask = useCallback(
    async (index: number, direction: 'up' | 'down') => {
      if (!selectedCategoryId) return;
      const newOrder = [...assignedTasks];
      const swapIndex = direction === 'up' ? index - 1 : index + 1;
      if (swapIndex < 0 || swapIndex >= newOrder.length) return;

      [newOrder[index], newOrder[swapIndex]] = [newOrder[swapIndex], newOrder[index]];
      const taskIds = newOrder.map((t) => t.id);

      await reorderMutation.mutateAsync({ categoryId: selectedCategoryId, taskIds });
    },
    [assignedTasks, selectedCategoryId, reorderMutation],
  );

  return (
    <div className="space-y-4">
      {/* Category Selector */}
      <div className="flex items-center gap-4">
        <div className="w-72">
          <Select
            value={selectedCategoryId ? String(selectedCategoryId) : ''}
            onValueChange={(v) => setSelectedCategoryId(Number(v))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a job category..." />
            </SelectTrigger>
            <SelectContent>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={String(cat.id)}>
                  {cat.name ?? `Category ${cat.id}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedCategoryId && (
          <Can action="create" subject="CategoryTask">
            <Button
              onClick={() => {
                setTaskSearch('');
                setAddDialogOpen(true);
              }}
              style={{ backgroundColor: '#E84A2B' }}
              className="text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Task
            </Button>
          </Can>
        )}
      </div>

      {/* No category selected */}
      {!selectedCategoryId && (
        <div className="text-center py-12 text-muted-foreground">
          <p>Select a job category above to manage its assigned tasks.</p>
        </div>
      )}

      {/* No permission to read category tasks */}
      {selectedCategoryId && !canReadTasks && (
        <div className="text-center py-12 text-muted-foreground">
          <p>You don't have permission to view category tasks.</p>
        </div>
      )}

      {/* Assigned tasks table */}
      {selectedCategoryId && canReadTasks && (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[60px]">Order</TableHead>
                <TableHead>Task Type</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="w-[140px]">Required</TableHead>
                {showActions && <TableHead className="w-[180px] text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {tasksLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={showActions ? 5 : 4}
                    className="text-center py-8 text-muted-foreground"
                  >
                    Loading...
                  </TableCell>
                </TableRow>
              ) : assignedTasks.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={showActions ? 5 : 4}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No tasks assigned to this category yet. Click "Add Task" to get started.
                  </TableCell>
                </TableRow>
              ) : (
                assignedTasks.map((task, idx) => (
                  <TableRow key={task.id}>
                    <TableCell>
                      <Badge variant="outline">{task.orderNumber ?? idx + 1}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{task.taskTypeName ?? '-'}</TableCell>
                    <TableCell>{task.type ?? '-'}</TableCell>
                    {/* Mandatory tasks must be completed before the operative can
                        finish the job on mobile; optional ones can be skipped.
                        Individual jobs can override this on the job's Tasks card. */}
                    <TableCell>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={task.isMandatory}
                          disabled={!canToggleMandatory || updateMutation.isPending}
                          onCheckedChange={(checked) =>
                            handleToggleMandatory(task.id, checked === true)
                          }
                          aria-label={`Mark ${task.taskTypeName ?? 'task'} as mandatory`}
                        />
                        <span className="text-xs text-muted-foreground">
                          {task.isMandatory ? 'Mandatory' : 'Optional'}
                        </span>
                      </label>
                    </TableCell>
                    {showActions && (
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Can action="update" subject="CategoryTask">
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={idx === 0 || reorderMutation.isPending}
                              onClick={() => moveTask(idx, 'up')}
                            >
                              <ArrowUp className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={
                                idx === assignedTasks.length - 1 || reorderMutation.isPending
                              }
                              onClick={() => moveTask(idx, 'down')}
                            >
                              <ArrowDown className="w-4 h-4" />
                            </Button>
                          </Can>
                          <Can action="delete" subject="CategoryTask">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setRemoveConfirmId(task.id)}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </Can>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add Task Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Task to Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search available task types..."
                value={taskSearch}
                onChange={(e) => setTaskSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="max-h-64 overflow-y-auto border rounded-lg">
              {availableTaskTypes.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  {taskSearch
                    ? 'No matching task types available'
                    : 'All task types are already assigned'}
                </div>
              ) : (
                <div className="divide-y">
                  {availableTaskTypes.map((tt) => (
                    <div
                      key={tt.id}
                      className="flex items-center justify-between px-4 py-3 hover:bg-accent/50"
                    >
                      <div>
                        <span className="text-sm font-medium">{tt.name}</span>
                        {tt.formAssignment && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            ({tt.formAssignment})
                          </span>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleAssign(tt.id)}
                        disabled={assignMutation.isPending}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Confirmation Dialog */}
      <Dialog open={removeConfirmId !== null} onOpenChange={() => setRemoveConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Task Assignment</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to remove this task from the category?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveConfirmId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => removeConfirmId && handleRemove(removeConfirmId)}
              disabled={removeMutation.isPending}
            >
              {removeMutation.isPending ? 'Removing...' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
